import { createContext, useContext, useMemo, useCallback, useReducer } from 'react';

import { v1 as uuidv1 } from 'uuid';

export const ModalContext = createContext();
ModalContext.displayName = 'ModalContext';
export const ModalDataContext = createContext();
ModalDataContext.displayName = 'ModalDataContext';

const useModalContext = () => {
  const context = useContext(ModalContext);

  if (typeof context === 'undefined') {
    throw new Error('useModalContext must be used within a ModalContext.Provider');
  }

  return context;
};

const useModalDataContext = () => {
  const context = useContext(ModalDataContext);

  if (typeof context === 'undefined') {
    throw new Error('useModalDataContext must be used within a ModalDataContext.Provider');
  }

  return context;
};

export function ModalProvider({ children }) {
  const [modals, dispatch] = useReducer((state, action) => {
    switch (action.type) {
      case 'open': {
        return [...state, action.payload];
      }
      case 'close':
        return [...state.filter((modal) => modal.name !== action.payload)];
      case 'clear':
        return [];
      default:
        return state;
    }
  }, []);

  const closeModal = (name) => {
    dispatch({ type: 'close', payload: name });
  };

  const openModal = useCallback(({ name, component, ...props }) => {
    const payload = {
      name: name ?? uuidv1(),
      component,
      props
    };

    dispatch({ type: 'open', payload });
  }, []);

  const clearAllModal = useCallback(() => {
    dispatch({ type: 'clear' });
  }, []);

  const value = useMemo(
    () => ({
      openModal,
      closeModal,
      clearAllModal
    }),
    [openModal, clearAllModal]
  );

  return (
    <ModalContext.Provider value={value}>
      <ModalDataContext.Provider value={modals}>{children}</ModalDataContext.Provider>
    </ModalContext.Provider>
  );
}

export default useModalContext;
export { useModalDataContext };
